import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import {
  columnAvatarString,
  defaultAddDeleteButton,
  defaultCellRenderer,
  displayPermissionNotAllowed,
  handleGridDeleteButtonState,
  lineThroughRenderer, showConfirmationDialog,
  showToast,
} from "../../../other/utils/utils.jsx";
import SwivuModal from "./SwivuModal.jsx";
import { useCallback } from "react";
import { DateHelper, MessageDialog, Popup } from "@bryntum/schedulerpro";
import { checklistItemImageDelete, importChecklistItemsApi, uploadImageToChecklistItem } from "../../../network/network.jsx";
import {
  cellMenuItemAddImage,
  cellMenuItemDeleteImage,
  cellMenuItemInsertAbove,
  cellMenuItemInsertBelow,
  cellMenuItemRemoveRow,
  processCellMenuItems
} from "../../../other/utils/gridCellMenuUtils.jsx";
import {
  assignViewsState,
  getEntityShareLink,
  pluralize,
  triggerNudgeAlert
} from "../../../other/utils/otherUtils.jsx";
import { ChecklistItems } from "../../../bryntum-lib/models/ChecklistItems.tsx";
import {
  DEEP_LINK_URL_KEYS, EDIT_MESSAGES,
  EDITOR_CONTEXT_MENU_ITEM_KEYS, VIEW_TYPES
} from "../../../other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";

const ALLOWED_IMAGE_TYPES = ["png", "jpeg", "jpg", "svg", "heif"];
const ALLOWED_CHECKLIST_ITEMS_IMPORT_TYPES = ["text/plain"];
class SwivuChecklistItemEditor extends EditorBase {
  static get $name() {
    return 'SwivuChecklistItemEditor'
  }

  static get type() {
    return WIDGET_KEYS.swivuChecklistItemEditorPopup;
  }

  static get configurable() {
    return {
      title: "Checklist Items",
      minWidth: "40em",
      width: "60em",
      minHeight: "35em",
      height: "35em",
      cls: "resize swivu-checklist-item-editor",
      closable: true,
      autoClose: false,
      draggable: true,
      // Enabling auto update fields, as changes from element editor are not reflected here
      autoUpdateFields: true,
      stateId: PERSISTENCE_STATE_KEYS.checklistItemEditor,
      bodyCls: "!bg-[#fff] dark:!bg-[#38373c] !gap-0",
      listeners: {
        beforeClose: "onCloseEditorHandler",
      },
      tbar: {
        cls: "!border-0 bg-[#ffffff] dark:bg-[#38373c] p-4",
        contentElementCls: "!p-0 flex-col !gap-[1em]",
        items: {
          titleInput: {
            type: "textfield",
            field: "title",
            name: "title",
            cls: "w-full checklist-items-title-input",
            placeholder: "Enter Checklist Title",
            onChange: "up.onTitleChange",
            required: true,
          },
          assignToField: {
            type: WIDGET_KEYS.settingsCombo,
            editMessage: EDIT_MESSAGES.user,
            name: "assignedBoardUsers",
            displayField: "name",
            valueField: "id",
            cls: "w-full checklist-assigned-users [&_.b-list]:!bg-[transparent]",
            placeholder: "To enable Checklist Assignments, assign this checklist to any number of board users.",
            clearable: true,
            hidePickerOnSelect: true,
            multiSelect: true,
            listItemTpl : (record) => columnAvatarString(record.name, record.profileImageURL),
            chipView: {
              itemTpl: (record) => columnAvatarString(record.name, record.profileImageURL),
              listeners: {
                item: "up.onClickLinkedEntity"
              }
            },
            onSwivuEditMessage: "onClickSwivuUserEditMessage",
            onChange: "up.onAssignedUsersChange",
            flex: 1
          },
        }
      },
      items: {
        checklistItemGrid: {
          cls: "w-full",
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          height: '100%',
          bodyCls: "!pt-0",
          stateId: PERSISTENCE_STATE_KEYS.checklistItemGrid,
          features: {
            stripe: true,
            rowReorder : {
              showGrip : true,
              swivuRowReorderFilterFn: ({ context }, record) => {
                const { checklistId } = context.insertBefore;
                return record.checklistId === checklistId;
              },
            },
            cellEdit: {
              addNewAtEnd: true,
              keyMap : {
                // we want to edit or add next row if user presses the Tab key, this will be the same behavior as Enter
                // copied the code from the CellEditor feature
                Tab: { handler : "finishAndEditNextRow", weight : 100 },
              },
            },
            cellMenu: {
              items: {
                cut: false,
                copy: false,
                paste: false,
                removeRow: cellMenuItemRemoveRow({ onItem: "up.onDeleteChecklistItemRecord" }),
                addImage: cellMenuItemAddImage({ weight: 500, onItem: "up.onAddImage" }),
                deleteImage: cellMenuItemDeleteImage({ weight: 600, onItem: "up.onRemoveImage" }),
                // Todo: Make it generic
                insertAbove: cellMenuItemInsertAbove({ weight: 700, canInsertAbove: true, onItem: "up.onInsertItem" }),
                insertBelow: cellMenuItemInsertBelow({ weight: 800, onItem: "up.onInsertItem" }),
              }
            },
            group: false,
            sort: false,
          },
          columns: [
            {
              id: "checklist-item-title",
              text: "Checklist Items",
              field: "description",
              flex: 1,
              cellCls: '!py-[1em]',
              renderer: ({ record, value }) => lineThroughRenderer(value, record.archived),
              editor: {
                placeholder: "Enter Checklist Item",
                type: "textarea"
              },
              autoHeight: true,
              finalizeCellEdit: "up.onFinalizeDescription",
            },
            { id: "checklist-item-image", altField: "description", field: "thumbnailUrl", type: WIDGET_KEYS.imageColumn, flex: 1 },
            { id: "checklist-item-comment", field: "comment", type: WIDGET_KEYS.commentColumn, flex: 1, cellCls: 'justify-start' },
            // Bug: Internally Check column uses record.set function to update the record's field instead of field's setter function if available.
            // Therefore, using toggle listener to manually call the setter function of `isItemCheck` defined in ChecklistItem model class
            {
              id: "checklist-item-checked",
              type: "check",
              text: "Checked",
              field: "isItemCheck",
              flex: 1,
              headerRenderer: () => '<i class="fa-light fa-square-check !m-0 text-[1.2em]"></i>',
              listeners: { toggle: "up.onChecklistItemCheck" }
            }
          ],
          listeners: {
            beforeDestroy: ({ source }) => {
              if (source.detacher) source.detacher();
              return true;
            },
            cellDblClick: "up.onDblClickCell",
            beforeCellEditStart: "up.onBeforeCellEditStart",
            selectionChange: "up.onGridSelectionChange"
          },
          store: {
            modelClass: ChecklistItems,
            reapplyFilterOnUpdate: true,
          },
        }
      },
      bbar: {
        cls: "dark:!bg-[#2c2a2e]",
        items: {
          dateMetaDataField: {
            type: "label",
            hidden: false,
            cls: 'mr-auto pl-[1em]'
          },
          ...defaultAddDeleteButton({
            addButton: {
              onClick: "up.onAddChecklistItem",
              cls: 'bg-[transparent] text-[#154d09] [&:not(.b-disabled)]:!text-[#228a0c] border-[1.5px] !rounded-md dark:border-[#373638]',
              text: "Add Item",
              icon: "b-fa b-fa-plus b-button-icon",
              tooltip: "Add Checklist Item"
            },
            deleteButton: {
              onClick: "up.onDeleteChecklistItemRecord",
              text: "Delete",
              cls: '!bg-[transparent] [&:not(.b-disabled):!text-[#f6971e] border-[1.5px] !rounded-md !border-[#dfdfdf] dark:!border-[#373638]',
              icon: "fa-light fa-trash-check",
              tooltip: "Delete Checklist Item",
              disabled: true,
            },
          }),
          nudgeBtn: {
            type: "button",
            icon: "fa-light fa-bulldozer",
            tooltip: "Nudge a Board User",
            cls: "bg-[transparent] text-[#1c4679] [&:not(.b-disabled)]:!text-[#3884e1] border-[1.5px] !rounded-md dark:border-[#373638]",
            onClick: "up.onClickNudge",
            text: "Nudge",
            disabled: true,
          },
          closeButton: {
            type: "button",
            icon: "fa-light fa-close",
            text: "Close",
            cls: 'bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md dark:border-[#373638]',
            tooltip: "Close Checklist Items",
            onClick: "up.onCloseEditorHandler"
          },
          fileField: {
            type: "filefield",
            accept: "image/*",
            hidden: true,
            onChange: "up.onHandleFileUpload"
          },
          importChecklistItemsField: {
            type: "filefield",
            accept: "text/plain",
            hidden: true,
            onChange: "up.onImportChecklistItem"
          }
        }
      }
    }
  }

  swivuRenderTitle(checklistRecord) {
    this.title = `<div class="flex justify-center items-center whitespace-nowrap gap-2 h-full">
      <span class="overflow-hidden text-ellipsis ml-[5.3em] text-[#7a7a7a] ${checklistRecord.archived ? 'line-through': ''}">${defaultCellRenderer({value: checklistRecord.entity?.name})} | ${defaultCellRenderer({value: checklistRecord.title})}</span>
    </div>`
  }

  onGridSelectionChange(e) {
    this.bbar.widgetMap.deleteButton.disabled = !e.selected.length;
  }

  onBeforeCellEditStart({ editorContext }) {
    if (!this.swivuCanDo && editorContext.column.id === "checklist-item-title") return false;
    const {checklistItemGrid} = this.widgetMap;
    const order = checklistItemGrid.store.last?.order + 1 || 0;
    checklistItemGrid.features.cellEdit.addNewAtEnd = this.createRecordFromMasterStore({ order });
    return true;
  }

  onImportChecklistItem(e) {
    const file = e.source.files?.[0];
    if (file) {
      if (file && ALLOWED_CHECKLIST_ITEMS_IMPORT_TYPES.includes(file.type)) {
        const { checklistItemGrid } = this.widgetMap;
        const fileReader = new FileReader();
        fileReader.onload = async ({ target: { result: checklistItemsData } }) => {
          try {
            const { data } = await importChecklistItemsApi(this.record.id, checklistItemsData, this.mask("Importing 0%"));
            this.project.applyChangeset({
              [STORE_KEYS.checklistItemStore]: { added: data[STORE_KEYS.checklistItemStore].rows }
            });

            checklistItemGrid.store.data = this.record.checklistItems;
            this.record.reRender = !this.record.reRender
          } catch (e) {
            console.log(e);
            MessageDialog.alert({
              title: "Error",
              message: e.message || "Internal Server Error. Please contact support for further assistance."
            });
          } finally {
            e.source.clear();
            e.source.value = null; // clearing this will call this import function again, so make sure to check for file length
            this.unmask();
          }
        };
        fileReader.readAsText(file);
      } else {
        MessageDialog.alert({
          title: "Error",
          message: `Unsupported File Type. Please upload files with extensions: ${ALLOWED_CHECKLIST_ITEMS_IMPORT_TYPES.join(", ")}`
        });
      }
    }
  }

  onCloseEditorHandler() {
    const {checklistItemGrid} = this.widgetMap;
    const phantomRecords = checklistItemGrid.store.query((record) => record.isPhantom, true);

    if (phantomRecords.length > 0) {
      showConfirmationDialog({
        title: `Discard empty checklist ${pluralize(phantomRecords.length, "item")}`,
        message: `You have ${phantomRecords.length} checklist ${pluralize(phantomRecords.length, "item")} where you did not </br> specify an action to be performed and checked.`,
        confirmBtnText: {
          cls: "bg-[#f6f6f6] text-[#cccccc] dark:bg-[#2e2e32] border-1 border-solid border-[#eaeaea] dark:border-[#3e3e41]",
          text: `Save & Close`
        },
        cancelBtnText: {
          cls: "order-1 b-raised",
          color: "b-blue",
          text: `Discard Empty ${pluralize(phantomRecords.length, "Item")}`,
        },
        isCloseByHeader: true,
      }).then((savedRecords) => {
        if (savedRecords) {
          this.project.checklistItemStore.add(phantomRecords);
        }
        if (this.swivuDeleteButtonListener) this.swivuDeleteButtonListener()
        if (this.swivuIdChangeListener) this.swivuIdChangeListener();
        this.hide();
      })
    } else {
      if (this.swivuDeleteButtonListener) this.swivuDeleteButtonListener();
      if (this.swivuIdChangeListener) this.swivuIdChangeListener();
      this.hide();
    }

    return false;
  }

  editRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams);
    const {
      checklistItemGrid,
      titleInput,
      assignToField,
      addButton,
      deleteButton,
      nudgeBtn,
    } = this.widgetMap;
    const { cellMenu, rowReorder } = checklistItemGrid.features;
    const { insertAbove, insertBelow, removeRow, addImage } = cellMenu.items;
    assignToField.store = context.project.boardUsersStore.query(() => true, true);
    assignToField.disabled = record.isPhantom;
    this.record = record;
    Object.assign(this, context, otherParams);
    this.loadContextMenu(record, context, context.user?.isDemoUser);
    this.swivuCanDo =  context.user.can(record.canEdit);
    const currentBoardUser = context.project.boardUsersStore.find((boardUser) => boardUser.userId === context.user.id);
    this.isChecklistAssigned = record.assignedBoardUsers.includes(currentBoardUser.id);
    assignViewsState([checklistItemGrid], context.stateProvider);

    if (checklistItemGrid.features.cellMenu) {
      checklistItemGrid.features.cellMenu.processItems = (e) => {
        processCellMenuItems(checklistItemGrid)

        const { items, record, selection  } = e;
        // we need to spread out the group children records as, when we try to archive the first record then the rest of the records never archived as the groupChildren array gets empty
        // spreading out into the new array will keep the original records in place
        // const selectedItems = [...(record.groupChildren || selection || [])];
        const isMultiSelect = record.isGroupHeader || selection.length > 1;

        const hasImage = Boolean(record.thumbnailUrl);
        items.addImage.text = hasImage ? "Replace Image" : "Add Image";
        items.addImage.hidden = items.insertAbove.hidden = items.insertBelow.hidden = isMultiSelect;
        items.deleteImage.hidden = !(hasImage && !isMultiSelect);
      };
    }
    titleInput.readOnly = assignToField.readOnly = addButton.disabled = deleteButton.disabled = insertAbove.disabled = insertBelow.disabled = removeRow.disabled = rowReorder.disabled = !this.swivuCanDo;
    nudgeBtn.disabled = !(record.assignedBoardUsers.length && this.swivuCanDo);
    checklistItemGrid.readOnly = addImage.disabled = !(this.swivuCanDo || this.isChecklistAssigned);
    // Assigning CRUD manager to the store to prevent crashes when records are reordered
    checklistItemGrid.store.swivuCrudManager = context.project;
    // Note: even though we have ordering considered in RowReorderExtendPlugin, still we don't receive the records in that same order
    if (!record.isPhantom) {
      checklistItemGrid.store.data = record.checklistItems.sort((a, b) => a.order - b.order);
    }
    // If record is not phantom
    this.displayRecordMetaInformation(record);
    this.swivuRenderTitle(record);
    this.isCreatingRecord();

    if (!record.title) {
      titleInput.focus();
    }
    if (!this.swivuDeleteButtonListener) {
      this.swivuDeleteButtonListener = handleGridDeleteButtonState(checklistItemGrid, deleteButton)
    }
  }

  onTitleChange({ value, userAction }) {
    this.record.title = value;
    this.swivuRenderTitle(this.record);
    if (userAction && this.record.isPhantom) {
      this.project.checklistStore.add(this.record)
      this.isCreatingRecord();
    }
  }

  displayRecordMetaInformation(record) {
    const bbar = this.widgetMap.bbar;
    if (bbar && bbar.getWidgetByRef("dateMetaDataField")) {
      const dateMetaDataField = bbar?.widgetMap?.dateMetaDataField;
      const { createdAt, updatedAt, updaterName } = record;

      if ((record.isPhantom || !dateMetaDataField || (!createdAt && !updatedAt))) {
        dateMetaDataField.hidden = true;
      }

      if (dateMetaDataField && !record.isPhantom) {
        const dateFormat = "M/D/YYYY";
        const commonCls = "text-[#6c6b6b] overflow-hidden overflow-ellipsis";
        dateMetaDataField.html = `
        <div class="overflow-hidden font-normal flex flex-col gap-[0.2em]">
          <span class="${commonCls}">Created: ${defaultCellRenderer({value: DateHelper.format(new Date(createdAt), dateFormat)})}</span>
          <span class="${commonCls}">Last Updated By: ${defaultCellRenderer({value: updaterName})}</span>
          <span class="${commonCls}">Last Updated: ${defaultCellRenderer({value: DateHelper.format(new Date(createdAt), dateFormat)})}</span>
        </div>
      `;
        dateMetaDataField.hidden = false;
      }
    }
  }

  onFinalizeDescription({ record, value }) {
    if (record.isPhantom && value) {
      this.project.checklistItemStore.add(record);
      this.record.reRender = !this.record.reRender;
    } else if (!value.trim()) {
      return "This field is required"
    }
    return true;
  }

  onAddImage({ record }) {
    if (!record.isPhantom && (this.swivuCanDo || this.isChecklistAssigned)) {
      const { fileField } = this.widgetMap;
      fileField.gridRecord = record;
      fileField.input.click();
    }
  }

  async onRemoveImage({ record }) {
    if (!record.isPhantom && (this.swivuCanDo || this.isChecklistAssigned)) {
      try {
        this.mask("Loading...");
        await checklistItemImageDelete(record.id);
        this.project.checklistItemStore.applyChangeset({
          updated: [{ id: record.id, thumbnailUrl: null, image: null }]
        });
      } catch (error) {
        showToast({ msg: "Something went wrong" })
        console.error('error', error);
      } finally {
        this.unmask();
      }
    }
  }

  onOpenImportChecklistItemsPicker() {
    if (this.swivuCanDo) {
      const { importChecklistItemsField } = this.widgetMap;
      importChecklistItemsField.input.click();
    }
  }

  async onHandleFileUpload({ source }) {
    if (source.files.length) {
      try {
        const [file] = source.files;
        this.mask("Loading...");
        if (file.type.includes("image") && ALLOWED_IMAGE_TYPES.includes(file.type.split("/")[1])) {
          const { data } = await uploadImageToChecklistItem(this.selectedBoard.id, source.gridRecord.id, file);
          this.project.checklistItemStore.applyChangeset({
            updated: [{ id: source.gridRecord.id, thumbnail_url: data.thumbnail_url + "?timestamp=" + Date.now(), image: data.image_url }]
          });
          this.record.reRender = !this.record.reRender;

        } else {
          throw Error(`Unsupported File Type. Please upload files with extensions: ${ALLOWED_IMAGE_TYPES.join(", ")}`);
        }
      } catch (err) {
        MessageDialog.alert({
          title: "Error",
          message: err.message || "Internal Server Error. Please contact support for further assistance."
        });
      } finally {
        this.unmask();
        source.clear();
        source.value = null;
      }
    }
  }

  onDblClickCell({column, record}) {
    if (column.type === WIDGET_KEYS.imageColumn) {
      if (record[column.field]) {
        const popup = new Popup({
          cls: "image-display",
          centered: true,
          draggable: true,
          autoClose: false,
          closeAction: "destroy",
          width: "100vw",
          height: "100vh",
          items: {
            closeBtn: {
              cls: "close-btn",
              type: "button",
              icon: "fa-light fa-close !text-[2em]",
              onClick: () => popup.destroy()
            },
            imageField: {
              type: "displayfield",
              cls: "image-display-field",
              name: "image",
              height: "100%",
              width: "100%",
              template: (value) => `<img alt="${record.image}" src="${value}" />`,
            }
          }
        })
        popup.record = record;
      }
      else {
        this.onAddImage({ record });
      }
    }
  }

  createRecordFromMasterStore(params = {}) {
    return this.project.checklistItemStore.createRecord({
      description: "",
      archived: this.record.archived,
      checked: false,
      checklist: this.record.id,
      ...params
    });
  }

  onAddChecklistItem() {
    // Todo: Not sure why this check is necessary as we only allow the editor to be opened only when checklist record is not phantom
    if (!this.record.isPhantom) {
      const {checklistItemGrid} = this.widgetMap;
      const order = checklistItemGrid.store.last?.order + 1 || 0;
      const checklistItemRecord = this.createRecordFromMasterStore({ order });
      checklistItemGrid.store.add([checklistItemRecord]);
      checklistItemGrid.startEditing({ record: checklistItemRecord, field: "description" })
    }
  }

  onDeleteChecklistItemRecord() {
    const { checklistItemGrid } = this.widgetMap;
    if (checklistItemGrid.selectedRecord) {
      if (this.swivuCanDo) {
        const selectedIds = checklistItemGrid.selectedRecords.map((r) => r.id);
        checklistItemGrid.store.remove(selectedIds);
        this.project.checklistItemStore.remove(selectedIds);
      } else {
        displayPermissionNotAllowed("delete", this.boardManager.selectedBoard.record.roleNames)
      }
    }
  }

  onChecklistItemCheck({ record, checked }) {
    record.isItemCheck = checked;
  }

  onInsertItem({ item, record, source }) {
    const { canInsertAbove } = item;
    const { store } = source;
    const recordIndex = store.records.findIndex((rec) => !rec.isGroupHeader && rec.id === record.id);
    // Cannot use `store.indexOf` because it returns index of group header records as well.
    if (recordIndex !== -1) {
      const insertPosition = canInsertAbove ? recordIndex : recordIndex + 1;
      const newRecord = this.createRecordFromMasterStore();
      store.insert(insertPosition ?? 0, [newRecord]);
      this.project.suspendEvents();
      this.project.beginBatch();
      store.forEach((record, index) => record.order = index);
      this.project.endBatch();
      this.project.resumeEvents();
      source.startEditing({ record: newRecord, field: "description" })
    }
  }

  getChecklistLink() {
    let entityType = "";
    if (this.record.taskId) entityType = DEEP_LINK_URL_KEYS.task;
    else if (this.record.resourceId) entityType = DEEP_LINK_URL_KEYS.resource;
    else if (this.record.clientId) entityType = DEEP_LINK_URL_KEYS.client;
    if (entityType) {
      return getEntityShareLink({
        record: this.record.entity,
        context: this,
        editorTabs: "checklist",
        entityType,
        otherKeys: { [DEEP_LINK_URL_KEYS.checklist]: this.record.id },
        entityDetail: { name: this.record.title }
      })
    }
  }

  loadContextMenu(record, context, isDemoUser) {
    const thisObj = this;
    this.configureEditorContextMenu(record, context, {
      recordStore: context.project.checklistStore,
      items: {
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.shareItem]: {
          enabled: !isDemoUser,
          editorParams() {
            return {
              message: thisObj.getChecklistLink(),
            }
          }
        },
      },
      newMenuItems: {
        importChecklistItem: {
          text: "Import Checklist Items",
          icon: "fa-solid fa-upload",
          onItem: "up.onOpenImportChecklistItemsPicker"
        }
      }
    })
    this.tools.contextMenu.menu.on("beforeShow", ({ source }) => {
      source.widgetMap.importChecklistItem.disabled = record.isPhantom || !this.swivuCanDo;
    });
  }

  onClickNudge() {
    triggerNudgeAlert(this.record.id, this.project, this)
  }

  onClickLinkedEntity({ record }) {
    this.editorHandler.openEditor(EDITOR_KEYS.userEditor, record, { user: record });
  }

  onClickSwivuUserEditMessage() {
    if (this.user && this.user.can(GRANTS.grant_four)) {
      this.viewManager.selectView(this.viewManager.getDefaultViewByType(VIEW_TYPES.users));
    }
  }

  onAssignedUsersChange({ value, userAction }) {
    if (userAction) this.record.assignedBoardUsers = value;
    this.widgetMap.nudgeBtn.disabled = !(this.record.assignedBoardUsers.length && this.swivuCanDo);
  }

  isCreatingRecord() {
    if (this.record.isPhantom) {
      const { assignToField, checklistItemGrid, addButton } = this.widgetMap;
      assignToField.disabled = addButton.disabled = true;
      const isRecordAddedInStore = this.project.checklistStore.find((record) => record.id === this.record.id, true);
      if (isRecordAddedInStore) {
        checklistItemGrid.mask(`Creating "${this.record.title}"`)
        if (!this.swivuIdChangeListener) {
          this.swivuIdChangeListener = this.project.checklistStore.on({
            idChange({ record: savedRecord }) {
              if (savedRecord.id === this.record.id) {
                this.displayRecordMetaInformation(this.record);
                assignToField.disabled = addButton.disabled = !this.swivuCanDo;
                checklistItemGrid.unmask();
                this.swivuIdChangeListener();
                this.swivuIdChangeListener = null;
              }
            },
            thisObj: this,
          })
        }
      }
    }
  }
}

SwivuChecklistItemEditor.initClass();

const SwivuChecklistItemPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const { checklist, ...rest } = record;
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.editRecord(checklist, context, Object.assign(otherParams, rest))
    editorInstance.editorRef = editor;
  }, [context, editorInstance, record]);

  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuChecklistItemEditorPopup}
      setFontSize={context.setFontSize}
      rowHeight={context.rowHeight}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  )
}

export default SwivuChecklistItemPopup
